// SchemePay Reconciliation Dashboard - High-Performance File Parser
import * as XLSX from 'xlsx';
import { RazorpayColumnMapping, ErpColumnMapping } from '../types/reconciliation';

export interface ParsedSheetData {
  headers: string[];
  rows: Record<string, any>[];
  fileName: string;
  rowCount: number;
}

/**
 * Parses XLSX, XLS, or CSV files into headers and JSON rows
 */
export async function parseSpreadsheetFile(file: File): Promise<ParsedSheetData> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: 'array',
    cellDates: true,
    cellNF: false,
    cellText: false,
  });

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error('Spreadsheet contains no sheets or data');
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const rawRows: any[] = XLSX.utils.sheet_to_json(worksheet, {
    raw: false,
    defval: '',
  });

  if (rawRows.length === 0) {
    throw new Error('File contains no rows of data');
  }

  const headers = Object.keys(rawRows[0]);

  return {
    headers,
    rows: rawRows,
    fileName: file.name,
    rowCount: rawRows.length,
  };
}

/**
 * Auto-detects Razorpay column headers based on typical naming conventions
 */
export function detectRazorpayColumns(headers: string[]): RazorpayColumnMapping {
  const findMatch = (candidates: string[]): string => {
    for (const cand of candidates) {
      const found = headers.find((h) =>
        h.toLowerCase().trim().replace(/[_\s-]/g, '') === cand.toLowerCase().replace(/[_\s-]/g, '')
      );
      if (found) return found;
    }
    // Partial substring fallback
    for (const cand of candidates) {
      const found = headers.find((h) =>
        h.toLowerCase().trim().includes(cand.toLowerCase())
      );
      if (found) return found;
    }
    return '';
  };

  return {
    paymentId: findMatch(['payment_id', 'payment id', 'paymentid', 'rp_payment_id', 'txn_id', 'transaction_id', 'pay_id', 'id']),
    amount: findMatch(['amount', 'payment_amount', 'paid_amount', 'txn_amount', 'total_amount']),
    customerName: findMatch(['customer_name', 'customer name', 'client_name', 'payer_name', 'name', 'user_name']),
    paymentDate: findMatch(['payment_date', 'payment date', 'created_at', 'txn_date', 'date', 'paid_at', 'timestamp']),
    status: findMatch(['status', 'payment_status', 'txn_status', 'state']),
    refundAmount: findMatch(['refund_amount', 'refunded_amount', 'refund_amt', 'amount_refunded']),
    refundDate: findMatch(['refund_date', 'refunded_at', 'refund_time']),
  };
}

/**
 * Auto-detects ERP column headers based on typical ERP naming conventions
 */
export function detectErpColumns(headers: string[]): ErpColumnMapping {
  const findMatch = (candidates: string[]): string => {
    for (const cand of candidates) {
      const found = headers.find((h) =>
        h.toLowerCase().trim().replace(/[_\s-]/g, '') === cand.toLowerCase().replace(/[_\s-]/g, '')
      );
      if (found) return found;
    }
    for (const cand of candidates) {
      const found = headers.find((h) =>
        h.toLowerCase().trim().includes(cand.toLowerCase())
      );
      if (found) return found;
    }
    return '';
  };

  return {
    paymentId: findMatch(['payment_id', 'payment id', 'rp_payment_id', 'razorpay_id', 'scheme_payment_id', 'txn_ref', 'payment_ref']),
    billNumber: findMatch(['bill_number', 'bill number', 'bill_no', 'invoice_no', 'inv_number', 'bill_id', 'voucher_no', 'doc_no']),
    billAmount: findMatch(['bill_amount', 'bill amount', 'invoice_amount', 'amount', 'item_amount', 'total_bill_amount', 'debit_amount']),
    customerName: findMatch(['customer_name', 'customer name', 'party_name', 'account_name', 'customer', 'name']),
    postingDate: findMatch(['posting_date', 'posting date', 'bill_date', 'invoice_date', 'voucher_date', 'doc_date', 'date']),
  };
}
